import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  Skeleton,
  Snackbar,
  Stack,
  Typography
} from '@mui/material';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import BusinessCenterRoundedIcon from '@mui/icons-material/BusinessCenterRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import NotesRoundedIcon from '@mui/icons-material/NotesRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import TodayRoundedIcon from '@mui/icons-material/TodayRounded';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { useAppSelector } from 'app/hooks';
import { differenceInSeconds, format, isToday, parseISO } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { AppDispatch, RootState } from 'store';
import { ClockInOutPayload, Shift } from 'types/ndisSection/clockInOutTypes';
import {
  clockIn,
  clockOut,
  fetchShifts,
  resetError
} from '../../../slices/NdisSlices/clockInOut/ClockInOutSlice';

const BRAND = '#0E9B8F';
const BRAND_DARK = '#006F68';
const SOFT_BG = '#F7FAFC';
const INK = '#070B2D';

const normalizeTimestamp = (timestamp?: string): string | undefined => {
  if (!timestamp) return undefined;
  return timestamp.replace(/T([0-9]{2})_([0-9]{2})_([0-9]{2})/, 'T$1:$2:$3');
};

const safeDate = (value?: string): Date | null => {
  if (!value) return null;
  try {
    return parseISO(normalizeTimestamp(value)!);
  } catch {
    return null;
  }
};

const fmtDate = (value?: string) => {
  const date = safeDate(value);
  return date ? format(date, 'dd MMM yyyy') : 'Not recorded';
};

const fmtTime = (value?: string) => {
  const date = safeDate(value);
  return date ? format(date, 'hh:mm a') : '--';
};

const fmtElapsed = (seconds: number) => {
  const hrs = Math.floor(seconds / 3600);
  const min = Math.floor((seconds % 3600) / 60);
  const sec = seconds % 60;
  return `${String(hrs).padStart(2, '0')}:${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const getPlannedHours = (shift: Shift | null): number => {
  const start = safeDate(shift?.start);
  const end = safeDate(shift?.end);
  if (!start || !end) return 0;
  return Math.max((end.getTime() - start.getTime()) / 36e5, 0);
};

const getStatus = (shift: Shift | null) => {
  if (!shift) return { label: 'Not started', color: '#2563EB', bg: '#DBEAFE' };
  if (shift.actual_end) return { label: 'Completed', color: '#059669', bg: '#D1FAE5' };
  if (shift.actual_start) return { label: 'In progress', color: '#D97706', bg: '#FEF3C7' };
  return { label: 'Not started', color: '#2563EB', bg: '#DBEAFE' };
};

const getInitials = (text?: string) => {
  if (!text) return 'S';
  return text
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
};

const SummaryTile = ({ icon, value, label }: { icon: React.ReactNode; value: string | number; label: string }) => (
  <Card
    sx={{
      flex: { xs: '1 1 calc(33.33% - 8px)', sm: '0 0 118px' },
      minWidth: { xs: 96, sm: 118 },
      borderRadius: { xs: 3, sm: 4 },
      boxShadow: '0 8px 28px rgba(15,23,42,0.08)',
      border: '1px solid #EDF2F7'
    }}
  >
    <CardContent
      sx={{
        py: { xs: 1.2, sm: 1.7 },
        px: { xs: 1.2, sm: 2 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: { xs: 'center', sm: 'flex-start' },
        gap: { xs: 0.8, sm: 1.5 }
      }}
    >
      <Box sx={{ color: '#24304F', display: 'flex', '& svg': { fontSize: { xs: 20, sm: 24 } } }}>{icon}</Box>
      <Box>
        <Typography sx={{ fontWeight: 900, color: INK, lineHeight: 1, fontSize: { xs: 14, sm: 16 } }}>{value}</Typography>
        <Typography variant="caption" sx={{ color: '#24304F', fontSize: { xs: 11, sm: 12 } }}>{label}</Typography>
      </Box>
    </CardContent>
  </Card>
);

const DetailBox = ({
  icon,
  label,
  value,
  subValue,
  tone = 'green'
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  subValue?: string;
  tone?: 'green' | 'red' | 'orange' | 'blue' | 'purple';
}) => {
  const palette = {
    green: { main: '#047857', bg: '#ECFDF5', border: '#A7F3D0', iconBg: '#D1FAE5' },
    red: { main: '#DC2626', bg: '#FEF2F2', border: '#FCA5A5', iconBg: '#FEE2E2' },
    orange: { main: '#EA580C', bg: '#FFF7ED', border: '#FDBA74', iconBg: '#FFEDD5' },
    blue: { main: '#2563EB', bg: '#EFF6FF', border: '#93C5FD', iconBg: '#DBEAFE' },
    purple: { main: '#7C3AED', bg: '#F5F3FF', border: '#C4B5FD', iconBg: '#EDE9FE' }
  }[tone];

  return (
    <Box
      sx={{
        border: `1.5px solid ${palette.border}`,
        borderRadius: 3,
        p: { xs: 1.35, sm: 1.8 },
        minHeight: { xs: 66, sm: 72 },
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, sm: 1.5 },
        bgcolor: palette.bg
      }}
    >
      <Avatar sx={{ width: { xs: 34, sm: 40 }, height: { xs: 34, sm: 40 }, bgcolor: palette.iconBg, color: palette.main, '& svg': { fontSize: { xs: 20, sm: 24 } } }}>
        {icon}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: palette.main,
            fontWeight: 900,
            fontSize: { xs: 11, sm: 12 },
            lineHeight: 1.1,
            display: 'block'
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            color: INK,
            fontWeight: 950,
            lineHeight: 1.25,
            fontSize: { xs: 14, sm: 16 },
            wordBreak: 'break-word'
          }}
        >
          {value}
        </Typography>
        {subValue && (
          <Typography
            variant="caption"
            sx={{
              color: '#334155',
              fontWeight: 700,
              lineHeight: 1.2,
              display: 'block',
              wordBreak: 'break-word'
            }}
          >
            {subValue}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const ShiftCard = ({ shift, selected, onClick }: { shift: Shift; selected: boolean; onClick: () => void }) => {
  const chipConfig = shift.actual_end
    ? {
        label: 'Completed',
        icon: <CheckCircleRoundedIcon />,
        color: '#047857',
        bg: '#D1FAE5'
      }
    : shift.actual_start
    ? {
        label: 'In Progress',
        icon: <PlayArrowRoundedIcon />,
        color: '#D97706',
        bg: '#FEF3C7'
      }
    : {
        label: 'Not Started',
        icon: <AccessTimeRoundedIcon />,
        color: '#2563EB',
        bg: '#DBEAFE'
      };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor: selected ? BRAND : '#E8EEF5',
        bgcolor: '#fff',
        overflow: 'visible',
        transition: 'all 0.2s ease',
        minHeight: { xs: 104, sm: 92 },
        boxShadow: selected ? '0 6px 20px rgba(14,155,143,0.12)' : 'none',
        '&:hover': {
          borderColor: BRAND,
          boxShadow: '0 8px 24px rgba(15,23,42,0.08)'
        }
      }}
    >
      <CardActionArea onClick={onClick} sx={{ borderRadius: 3, display: 'block' }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 1.7 }, '&:last-child': { pb: { xs: 1.5, sm: 1.7 } } }}>
          <Stack
            direction="row"
            spacing={{ xs: 1.25, sm: 1.5 }}
            alignItems="center"
            sx={{ width: '100%', minHeight: { xs: 74, sm: 58 } }}
          >
            <Avatar
              sx={{
                width: { xs: 44, sm: 48 },
                height: { xs: 44, sm: 48 },
                bgcolor: '#E8F6F4',
                color: BRAND,
                fontWeight: 900,
                flexShrink: 0
              }}
            >
              {getInitials(shift.client_name || shift.title)}
            </Avatar>

            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <Typography sx={{ fontWeight: 900, color: INK, fontSize: { xs: 15, sm: 16 }, lineHeight: 1.25 }} noWrap>
                {shift.title || 'Untitled shift'}
              </Typography>

              <Typography variant="body2" sx={{ color: '#4C587A', fontSize: { xs: 13, sm: 14 }, lineHeight: 1.25 }} noWrap>
                {shift.client_name || 'Client not available'}
              </Typography>

              <Stack
                direction="row"
                spacing={0.7}
                alignItems="center"
                sx={{ mt: 0.7, color: '#4C587A', maxWidth: '100%', overflow: 'hidden' }}
              >
                <CalendarMonthRoundedIcon sx={{ fontSize: 14, flexShrink: 0 }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: 11.5, sm: 12 },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {fmtDate(shift.start)} • {fmtTime(shift.start)} – {fmtTime(shift.end)}
                </Typography>
              </Stack>
            </Box>

            <Chip
              size="small"
              icon={chipConfig.icon}
              label={chipConfig.label}
              sx={{
                flexShrink: 0,
                minWidth: { xs: 92, sm: 112 },
                maxWidth: { xs: 104, sm: 130 },
                height: { xs: 28, sm: 30 },
                justifyContent: 'center',
                borderRadius: '999px',
                fontWeight: 900,
                fontSize: { xs: 10.5, sm: 12 },
                color: chipConfig.color,
                bgcolor: chipConfig.bg,
                '& .MuiChip-icon': {
                  color: chipConfig.color,
                  fontSize: { xs: 16, sm: 18 },
                  ml: { xs: 0.5, sm: 0.75 }
                },
                '& .MuiChip-label': {
                  px: { xs: 0.5, sm: 0.75 },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }
              }}
            />
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const LoadingList = () => (
  <Stack spacing={1.5}>{[1, 2, 3, 4].map((item) => <Skeleton key={item} variant="rounded" height={82} sx={{ borderRadius: 3 }} />)}</Stack>
);

const ClockInOut: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedClient, pathname } = location.state || {};

  const selectedProjectId = useAppSelector((state) => state.homeTilesServices.selectedProjectId);
  const selectedServiceTitle = useAppSelector((state) => state.serviceSelected.selectedServiceTitle);

  const userDetails = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user-details') || '{}');
    } catch {
      return {};
    }
  }, []);

  const basePayload = useMemo(() => ({
    group_id: selectedProjectId || '',
    service_name_id: selectedServiceTitle || '',
    component_parent_id: pathname || '/profile/weekly-patients-schedule',
    nested_component_id: 'weekly-patients-schedule',
    user_whoRequested_id: selectedClient?.label || '',
    loggedIN_user_id: userDetails?.name || '',
    loggedIN_user_email: userDetails?.email || ''
  }), [selectedProjectId, selectedServiceTitle, pathname, selectedClient?.label, userDetails?.name, userDetails?.email]);

  const { shifts, loading, error } = useSelector((state: RootState) => state.clockInOut);

  const [selectedShiftId, setSelectedShiftId] = useState('');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [clockInLoading, setClockInLoading] = useState(false);
  const [clockOutLoading, setClockOutLoading] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [filter, setFilter] = useState<'All' | 'Not Started' | 'Completed'>('All');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const sortedShifts = useMemo(() => [...shifts].sort((a, b) => (safeDate(a.start)?.getTime() || 0) - (safeDate(b.start)?.getTime() || 0)), [shifts]);
  const selectedShift = useMemo(() => sortedShifts.find((shift) => shift.id === selectedShiftId) || null, [sortedShifts, selectedShiftId]);
  const activeShift = useMemo(() => sortedShifts.find((shift) => shift.actual_start && !shift.actual_end) || null, [sortedShifts]);
  const todaysCount = useMemo(() => sortedShifts.filter((shift) => { const d = safeDate(shift.start); return d ? isToday(d) : false; }).length, [sortedShifts]);
  const completedCount = useMemo(() => sortedShifts.filter((shift) => shift.actual_end).length, [sortedShifts]);

  const filteredShifts = useMemo(() => {
    if (filter === 'Completed') return sortedShifts.filter((shift) => shift.actual_end);
    if (filter === 'Not Started') return sortedShifts.filter((shift) => !shift.actual_start && !shift.actual_end);
    return sortedShifts;
  }, [filter, sortedShifts]);

  useEffect(() => {
    if (!basePayload.group_id || !basePayload.service_name_id) return;
    dispatch(fetchShifts(basePayload));
  }, [dispatch, basePayload]);

  useEffect(() => {
    if (!sortedShifts.length) return;
    setSelectedShiftId((current) => {
      if (current && sortedShifts.some((shift) => shift.id === current)) return current;
      const todayShift = sortedShifts.find((shift) => { const d = safeDate(shift.start); return d ? isToday(d) : false; });
      return activeShift?.id || todayShift?.id || sortedShifts[0].id;
    });
  }, [sortedShifts, activeShift?.id]);

  useEffect(() => {
    if (!selectedShift?.actual_start) {
      setElapsedSeconds(0);
      return;
    }
    const start = safeDate(selectedShift.actual_start);
    const end = selectedShift.actual_end ? safeDate(selectedShift.actual_end) : new Date();
    setElapsedSeconds(start && end ? Math.max(differenceInSeconds(end, start), 0) : 0);
  }, [selectedShift?.id, selectedShift?.actual_start, selectedShift?.actual_end]);

  useEffect(() => {
    if (!selectedShift?.actual_start || selectedShift.actual_end) return;
    const interval = window.setInterval(() => {
      const start = safeDate(selectedShift.actual_start);
      setElapsedSeconds(start ? Math.max(differenceInSeconds(new Date(), start), 0) : 0);
    }, 1000);
    return () => window.clearInterval(interval);
  }, [selectedShift?.actual_start, selectedShift?.actual_end]);

  const plannedHours = getPlannedHours(selectedShift);
  const actualHours = selectedShift?.actual_worked_hours || (elapsedSeconds / 3600).toFixed(2);
  const progress = plannedHours > 0 ? Math.min((elapsedSeconds / (plannedHours * 3600)) * 100, 100) : 0;
  const status = getStatus(selectedShift);
  const isActive = !!selectedShift?.actual_start && !selectedShift?.actual_end;
  const isCompleted = !!selectedShift?.actual_end;

  const buildPayload = useCallback((shift: Shift, updates: Partial<ClockInOutPayload>): ClockInOutPayload => ({
    ...basePayload,
    shiftId: shift.id,
    userId: basePayload.loggedIN_user_id,
    event_id: shift.id,
    title: shift.title,
    description: shift.description || '',
    start: shift.start,
    end: shift.end,
    support_item_number: shift.support_item_number || '',
    support_worder: shift.support_worder || '',
    manager: shift.manager || '',
    client_name: shift.client_name || '',
    color: shift.color || BRAND,
    textColor: shift.textColor || '',
    allDay: shift.allDay || false,
    ...updates
  }), [basePayload]);

  const handleClockIn = async () => {
    if (!selectedShift) return setSnackbar({ open: true, message: 'Please select a shift first.', severity: 'error' });
    if (activeShift && activeShift.id !== selectedShift.id) return setSnackbar({ open: true, message: 'Another shift is already in progress. Please clock out first.', severity: 'error' });

    setClockInLoading(true);
    try {
      const now = new Date().toISOString();
      const payload = buildPayload(selectedShift, { clockInTime: now, actual_start: now, actual_worked_hours: '0' });
      await dispatch(clockIn({ shift: selectedShift, clockInTime: now, payload })).unwrap();
      await dispatch(fetchShifts(basePayload)).unwrap();
      setSelectedShiftId(selectedShift.id);
      setSnackbar({ open: true, message: 'Clocked in successfully.', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.message || 'Failed to clock in.', severity: 'error' });
    } finally {
      setClockInLoading(false);
    }
  };

  const handleClockOut = async () => {
    if (!selectedShift?.actual_start) return setSnackbar({ open: true, message: 'This shift has not been started.', severity: 'error' });

    setClockOutLoading(true);
    try {
      const now = new Date().toISOString();
      const workedHours = ((new Date(now).getTime() - new Date(selectedShift.actual_start).getTime()) / 36e5).toFixed(2);
      const payload = buildPayload(selectedShift, { clockOutTime: now, actual_end: now, actual_worked_hours: workedHours });
      await dispatch(clockOut({ shift: selectedShift, clockOutTime: now, clockInTime: selectedShift.actual_start, payload })).unwrap();
      await dispatch(fetchShifts(basePayload)).unwrap();
      setSnackbar({ open: true, message: 'Clocked out successfully.', severity: 'success' });
      setNotesDialogOpen(true);
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.message || 'Failed to clock out.', severity: 'error' });
    } finally {
      setClockOutLoading(false);
    }
  };

  const handleCloseSnack = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
    dispatch(resetError());
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: SOFT_BG, p: { xs: 1.25, sm: 2, lg: 3 }, overflowX: 'hidden' }}>
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2}>
          <Stack direction="row" spacing={{ xs: 1.2, sm: 1.5 }} alignItems="center">
            <Avatar sx={{ width: { xs: 54, sm: 72 }, height: { xs: 54, sm: 72 }, borderRadius: { xs: 3, sm: 4 }, bgcolor: '#EAF8F6', color: BRAND, boxShadow: '0 8px 30px rgba(14,155,143,0.18)' }}>
              <AccessTimeRoundedIcon sx={{ fontSize: { xs: 30, sm: 38 } }} />
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ color: INK, fontWeight: 950, letterSpacing: -0.8, fontSize: { xs: 24, sm: 34 } }}>Shift Clock In / Out</Typography>
              <Typography sx={{ color: '#3F4A6B', fontWeight: 600, fontSize: { xs: 13, sm: 16 } }}>View all assigned shifts, start work, track time, and clock out from one screen.</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={{ xs: 1, sm: 2 }} sx={{ width: { xs: '100%', md: 'auto' }, flexWrap: 'wrap' }}>
            <SummaryTile icon={<CalendarMonthRoundedIcon />} value={todaysCount} label="Today" />
            <SummaryTile icon={<BusinessCenterRoundedIcon />} value={sortedShifts.length} label="Total" />
            <SummaryTile icon={<CheckCircleOutlineRoundedIcon />} value={completedCount} label="Completed" />
          </Stack>
        </Stack>

        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} md={5} lg={3.6}>
            <Card sx={{ height: '100%', borderRadius: 4, boxShadow: '0 18px 50px rgba(15,23,42,0.08)', border: '1px solid #EEF2F7' }}>
              <CardContent sx={{ p: { xs: 1.75, sm: 2.5 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ color: INK, fontWeight: 950 }}>My Shifts</Typography>
                  <Button variant="outlined" sx={{ minWidth: 44, width: 44, height: 44, borderRadius: 3, borderColor: '#E1E8F0', color: '#24304F' }}>
                    <FilterAltOutlinedIcon />
                  </Button>
                </Stack>
                <Stack direction="row" spacing={{ xs: 1.5, sm: 3 }} sx={{ mb: 2.2, borderBottom: '1px solid #E7EDF5', overflowX: 'auto' }}>
                  {(['All', 'Not Started', 'Completed'] as const).map((item) => (
                    <Button key={item} onClick={() => setFilter(item)} sx={{ px: 0, flexShrink: 0, fontSize: { xs: 12, sm: 14 }, color: filter === item ? BRAND : '#3F4A6B', fontWeight: 800, borderRadius: 0, borderBottom: filter === item ? `2px solid ${BRAND}` : '2px solid transparent' }}>
                      {item}
                    </Button>
                  ))}
                </Stack>
                {loading && !sortedShifts.length ? <LoadingList /> : (
                  <Stack spacing={1.5} sx={{ maxHeight: { xs: 460, sm: 520, lg: 590 }, overflowY: 'auto', overflowX: 'hidden', pr: 0.5, pb: 0.5 }}>
                    {filteredShifts.slice(0, 6).map((shift) => <ShiftCard key={shift.id} shift={shift} selected={shift.id === selectedShiftId} onClick={() => setSelectedShiftId(shift.id)} />)}
                    {filteredShifts.length > 6 && <Button fullWidth variant="outlined" sx={{ py: 1.5, borderRadius: 3, color: INK, borderColor: '#E1E8F0', fontWeight: 900 }}>View all shifts</Button>}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={7} lg={4.4}>
            <Stack spacing={3}>
              <Card sx={{ borderRadius: 4, color: '#fff', overflow: 'hidden', boxShadow: '0 18px 50px rgba(0,111,104,0.25)', background: `radial-gradient(circle at 50% 45%, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0) 45%), linear-gradient(135deg, ${BRAND_DARK}, #008D83)` }}>
                <CardContent sx={{ p: { xs: 1.75, sm: 3 }, minHeight: { xs: 'auto', sm: 560, lg: 610 }, position: 'relative' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Chip
                      icon={isActive ? <PlayArrowRoundedIcon /> : <AccessTimeRoundedIcon />}
                      label={isActive ? 'Clock running' : status.label}
                      sx={{ bgcolor: isActive ? 'rgba(209,250,229,0.22)' : 'rgba(255,255,255,0.14)', color: '#fff', fontWeight: 900, px: 1, maxWidth: { xs: 170, sm: 'none' }, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                    />
                    <MoreVertRoundedIcon />
                  </Stack>
                  <Box
                    sx={{
                      width: { xs: 238, sm: 340, lg: 400 },
                      maxWidth: '100%',
                      aspectRatio: '1 / 1',
                      mx: 'auto',
                      mt: { xs: 1.5, sm: 2 },
                      borderRadius: '50%',
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background:
                        'conic-gradient(from 20deg, #19D4C4 0deg, #19D4C4 var(--progress), rgba(255,255,255,0.18) var(--progress), rgba(255,255,255,0.18) 360deg)',
                      '--progress': `${isCompleted ? 360 : progress * 3.6}deg`
                    }}
                  >
                    <Box sx={{ position: 'absolute', inset: { xs: 12, sm: 16, lg: 18 }, borderRadius: '50%', bgcolor: '#00786F' }} />
                    <Box sx={{ zIndex: 1, textAlign: 'center' }}>
                      <Typography sx={{ fontWeight: 700, fontSize: { xs: 15, sm: 18 } }}>Elapsed Time</Typography>
                      <Typography
                        sx={{
                          fontWeight: 950,
                          fontSize: { xs: 34, sm: 56, lg: 68 },
                          letterSpacing: { xs: -0.8, sm: -2 },
                          lineHeight: 1.05
                        }}
                      >
                        {fmtElapsed(elapsedSeconds)}
                      </Typography>
                      <Stack direction="row" justifyContent="center" spacing={{ xs: 2.5, sm: 6 }} sx={{ opacity: 0.92 }}>
                        <Typography variant="caption" sx={{ fontSize: { xs: 10, sm: 12 } }}>HRS</Typography>
                        <Typography variant="caption" sx={{ fontSize: { xs: 10, sm: 12 } }}>MIN</Typography>
                        <Typography variant="caption" sx={{ fontSize: { xs: 10, sm: 12 } }}>SEC</Typography>
                      </Stack>
                      <Typography sx={{ mt: { xs: 2.5, sm: 4 }, fontWeight: 900, fontSize: { xs: 16, sm: 20 } }}>
                        {isActive ? 'Clock is running — shift in progress' : isCompleted ? 'Shift completed' : 'Ready to start your shift'}
                      </Typography>
                    </Box>
                  </Box>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ px: { xs: 0, md: 2 }, mt: 1 }}>
                    <Button fullWidth variant="contained" startIcon={clockInLoading ? <CircularProgress size={18} /> : <PlayArrowRoundedIcon />} onClick={handleClockIn} disabled={!selectedShift || isActive || isCompleted || clockInLoading || clockOutLoading} sx={{ bgcolor: '#fff', color: BRAND, borderRadius: 4, py: { xs: 1.25, sm: 1.7 }, fontWeight: 950, fontSize: { xs: 15, sm: 18 }, '&:hover': { bgcolor: '#F2FFFC' } }}>Clock In</Button>
                    <Button fullWidth variant="outlined" startIcon={clockOutLoading ? <CircularProgress size={18} /> : <LogoutRoundedIcon />} onClick={handleClockOut} disabled={!isActive || clockInLoading || clockOutLoading} sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.35)', borderRadius: 4, py: { xs: 1.25, sm: 1.7 }, fontWeight: 950, fontSize: { xs: 15, sm: 18 }, '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}>Clock Out</Button>
                  </Stack>
                  <Box sx={{ mt: { xs: 2, sm: 3 }, p: { xs: 1.25, sm: 2 }, borderRadius: 4, bgcolor: 'rgba(0,64,60,0.45)' }}>
                    <Grid container spacing={{ xs: 1, sm: 1 }}>
                      <Grid item xs={12} sm={4}>
                        <DetailBox
                          tone="red"
                          icon={<AccessTimeRoundedIcon />}
                          label="Actual In"
                          value={selectedShift?.actual_start ? fmtTime(selectedShift.actual_start) : '--'}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <DetailBox
                          tone="orange"
                          icon={<LogoutRoundedIcon />}
                          label={isActive ? 'Expected Out' : 'Actual Out'}
                          value={isActive ? fmtTime(selectedShift?.end) : selectedShift?.actual_end ? fmtTime(selectedShift.actual_end) : '--'}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <DetailBox
                          tone="blue"
                          icon={<TimerOutlinedIcon />}
                          label="Actual Duration"
                          value={`${actualHours} hrs`}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
              <Card sx={{ borderRadius: 4, boxShadow: '0 18px 50px rgba(15,23,42,0.08)' }}>
                <CardContent sx={{ px: 3, py: 2.5 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 5 }} divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />}>
                    <DetailBox icon={<AccessTimeRoundedIcon />} label="Planned Hours" value={`${plannedHours.toFixed(2)} hrs`} />
                    <DetailBox icon={<TimerOutlinedIcon />} label="Actual Hours" value={`${actualHours} hrs`} />
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Stack spacing={3}>
              <Card sx={{ borderRadius: 4, boxShadow: '0 18px 50px rgba(15,23,42,0.08)', border: '1px solid #EEF2F7' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                    <DescriptionRoundedIcon sx={{ color: BRAND }} />
                    <Typography variant="h6" sx={{ color: INK, fontWeight: 950 }}>Shift Details</Typography>
                  </Stack>
                  <Divider sx={{ mb: 2, width: 70, borderColor: BRAND, borderBottomWidth: 2 }} />
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}><DetailBox icon={<PersonRoundedIcon />} label="Client" value={selectedShift?.client_name || 'Not recorded'} subValue={selectedShift?.support_worder || selectedShift?.manager || ''} /></Grid>
                    <Grid item xs={12} sm={6}><DetailBox icon={<CalendarMonthRoundedIcon />} label="Date" value={fmtDate(selectedShift?.start)} /></Grid>
                    <Grid item xs={12} sm={6}><DetailBox icon={<AccessTimeRoundedIcon />} label="Planned Start" value={fmtTime(selectedShift?.start)} /></Grid>
                    <Grid item xs={12} sm={6}><DetailBox icon={<AccessTimeRoundedIcon />} label="Planned End" value={fmtTime(selectedShift?.end)} /></Grid>
                    <Grid item xs={12} sm={6}><DetailBox icon={<LoginRoundedIcon />} label="Planned Hours" value={`${plannedHours.toFixed(2)} hrs`} /></Grid>
                    <Grid item xs={12} sm={6}><DetailBox icon={<LogoutRoundedIcon />} label="Status" value={status.label} /></Grid>
                    <Grid item xs={12} sm={6}><DetailBox tone="red" icon={<LoginRoundedIcon />} label="Actual Clock In" value={selectedShift?.actual_start ? fmtTime(selectedShift.actual_start) : 'Not recorded'} /></Grid>
                    <Grid item xs={12} sm={6}><DetailBox tone="orange" icon={<LogoutRoundedIcon />} label={isActive ? 'Expected Clock Out' : 'Actual Clock Out'} value={isActive ? fmtTime(selectedShift?.end) : selectedShift?.actual_end ? fmtTime(selectedShift.actual_end) : 'Not recorded'} /></Grid>
                    <Grid item xs={12}><DetailBox tone="blue" icon={<TimerOutlinedIcon />} label="Actual Duration" value={`${actualHours} hrs`} /></Grid>
                  </Grid>
                </CardContent>
              </Card>
              <Card sx={{ borderRadius: 4, bgcolor: '#EAF8F6', border: '1px solid #C8EFEB', boxShadow: 'none' }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <TipsAndUpdatesOutlinedIcon sx={{ color: BRAND }} />
                    <Box>
                      <Typography sx={{ color: INK, fontWeight: 950, mb: 1 }}>Tip</Typography>
                      <Typography sx={{ color: INK }}>Clock in at the start of your shift and clock out when you're done.</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      </Stack>

      <Dialog open={notesDialogOpen} onClose={() => setNotesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Shift Notes</DialogTitle>
        <DialogContent><Typography>Would you like to complete shift notes for this shift?</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesDialogOpen(false)}>Later</Button>
          <Button variant="contained" onClick={() => { setNotesDialogOpen(false); navigate('/profile/ShiftNotes', { state: { selectedShift } }); }}>Fill Notes</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open || !!error} autoHideDuration={5000} onClose={handleCloseSnack} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnack} severity={error ? 'error' : snackbar.severity} sx={{ width: '100%' }}>{error || snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ClockInOut;




















// import {
//   Alert,
//   Box,
//   Button,
//   Card,
//   CardContent,
//   CircularProgress,
//   Dialog,
//   DialogActions,
//   DialogContent,
//   DialogTitle,
//   Divider,
//   FormControl,
//   Grid,
//   IconButton,
//   InputLabel,
//   MenuItem,
//   Select,
//   Snackbar,
//   SvgIconProps,
//   Tooltip,
//   Typography,
//   useMediaQuery,
//   useTheme
// } from '@mui/material';
// import AlarmIcon from '@mui/icons-material/Alarm';
// import CloseIcon from '@mui/icons-material/Close';
// import EventIcon from '@mui/icons-material/Event';
// import PlayArrowIcon from '@mui/icons-material/PlayArrow';
// import StopIcon from '@mui/icons-material/Stop';
// import TableViewIcon from '@mui/icons-material/TableView';
// import WorkIcon from '@mui/icons-material/Work';
// import { useAppSelector } from 'app/hooks';
// import 'chart.js/auto';
// import { format, parseISO, differenceInSeconds } from 'date-fns';
// import React, { useEffect, useMemo, useState } from 'react';
// import { Bar } from 'react-chartjs-2';
// import { useDispatch, useSelector } from 'react-redux';
// import { useLocation, useNavigate } from 'react-router';
// import { AppDispatch, RootState } from 'store';
// import { ClockInOutPayload, Shift } from 'types/ndisSection/clockInOutTypes';
// import {
//   clockIn,
//   clockOut,
//   fetchShifts,
//   resetError
// } from '../../../slices/NdisSlices/clockInOut/ClockInOutSlice';
// import ShiftDetailsTable from '../shiftDetails/ShiftDetailsTable';
// import OtherDetails from './OtherDetails';

// // Utility function to normalize timestamps
// const normalizeTimestamp = (timestamp: string | undefined): string | undefined => {
//   if (!timestamp) return undefined;
//   return timestamp.replace(/T(\d{2})_(\d{2})_(\d{2})/, 'T$1:$2:$3');
// };

// // Section component
// interface SectionProps {
//   title: string;
//   icon: React.ComponentType<SvgIconProps>;
//   color: string;
//   items: { label?: string; value: string }[];
// }

// const Section: React.FC<SectionProps> = ({ title, icon: Icon, color, items }) => (
//   <Box>
//     <Typography
//       variant="h6"
//       sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', mb: 2, color }}
//     >
//       <Icon sx={{ mr: 1 }} />
//       {title}
//     </Typography>
//     <Divider sx={{ mb: 2 }} />
//     {items.map((item, index) => (
//       <Box sx={{ display: 'flex', mb: index !== items.length - 1 ? 1 : 0 }} key={index}>
//         {item.label && (
//           <Typography
//             variant="body2"
//             sx={{ color: '#757575', fontWeight: 'bold', flex: '0 0 150px' }}
//           >
//             {item.label}:
//           </Typography>
//         )}
//         <Typography variant="body1">{item.value}</Typography>
//       </Box>
//     ))}
//   </Box>
// );

// // JobDetails component
// const JobDetails = ({
//   shift,
//   upcomingShift,
//   handleOpenModal,
//   formatDate
// }: {
//   shift: Shift | null;
//   upcomingShift: Shift | null;
//   handleOpenModal: () => void;
//   formatDate: (isoTimestamp: string | undefined) => string;
// }) => {
//   const formatISOTimestamp = (isoTimestamp: string | undefined): string => {
//     if (!isoTimestamp) return '';
//     try {
//       const normalized = normalizeTimestamp(isoTimestamp);
//       return format(parseISO(normalized!), 'dd/MM/yy, hh:mm a');
//     } catch {
//       return 'Invalid time';
//     }
//   };

//   const calculateTotalHoursProjected = (shift: Shift | null): string => {
//     if (!shift?.start || !shift?.end) return '0';
//     try {
//       const normalizedStart = normalizeTimestamp(shift.start);
//       const normalizedEnd = normalizeTimestamp(shift.end);
//       const start = parseISO(normalizedStart!);
//       const end = parseISO(normalizedEnd!);
//       const diffInMs = end.getTime() - start.getTime();
//       return (diffInMs / (1000 * 60 * 60)).toFixed(2);
//     } catch {
//       return '0';
//     }
//   };

//   const sections = [
//     {
//       title: 'Work Updates',
//       icon: WorkIcon,
//       color: '#4caf50',
//       items: [
//         { label: 'Client Name', value: shift?.client_name || 'N/A' },
//         {
//           label: 'Planned Start',
//           value: shift?.start ? formatISOTimestamp(shift.start) : ''
//         },
//         {
//           label: 'Planned End',
//           value: shift?.end ? formatISOTimestamp(shift.end) : ''
//         },
//         {
//           label: 'Actual Start',
//           value: shift?.actual_start ? formatISOTimestamp(shift.actual_start) : 'Not started'
//         },
//         {
//           label: 'Actual End',
//           value: shift?.actual_end ? formatISOTimestamp(shift.actual_end) : 'Not completed'
//         },
//         {
//           label: 'Planned Hours',
//           value: shift ? `${calculateTotalHoursProjected(shift)} hours` : '0 hours'
//         },
//         {
//           label: 'Hours Worked',
//           value: shift?.actual_worked_hours ? `${shift.actual_worked_hours} hours` : '0 hours'
//         },
//         { label: 'Date', value: shift?.start ? formatDate(shift.start) : '' }
//       ]
//     },
//     {
//       title: 'Upcoming Shift',
//       icon: AlarmIcon,
//       color: '#ff9800',
//       items: [
//         { label: 'Client Name', value: upcomingShift?.client_name || 'N/A' },
//         {
//           label: 'Start Time',
//           value: upcomingShift?.start ? formatISOTimestamp(upcomingShift.start) : ''
//         },
//         {
//           label: 'End Time',
//           value: upcomingShift?.end ? formatISOTimestamp(upcomingShift.end) : ''
//         },
//         { label: 'Date', value: upcomingShift?.start ? formatDate(upcomingShift.start) : '' },
//         {
//           label: 'Total hours projected',
//           value: upcomingShift ? `${calculateTotalHoursProjected(upcomingShift)} hours` : '0 hours'
//         }
//       ]
//     },
//     {
//       title: 'Planned Leave',
//       icon: EventIcon,
//       color: '#9c27b0',
//       items: [
//         { label: 'Date', value: '' },
//         { label: 'Start Time', value: '' },
//         { label: 'End Time', value: '' },
//         { label: 'Total hours projected', value: '' }
//       ]
//     }
//   ];

//   return (
//     <Card
//       sx={{ boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)', borderRadius: 4, m: { xs: 1, md: 0 } }}
//     >
//       <CardContent sx={{ p: { xs: 2, md: 3 } }}>
//         <Box
//           display="flex"
//           justifyContent="space-between"
//           alignItems="center"
//           flexWrap="wrap"
//           mb={2}
//         >
//           <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
//             Job Details
//           </Typography>
//           <Box>
//             {shift ? (
//               <IconButton color="primary" onClick={handleOpenModal} sx={{ mt: 1 }}>
//                 <TableViewIcon />
//               </IconButton>
//             ) : (
//               <Typography variant="body1" sx={{ color: '#757575', mt: 1 }}>
//                 No shift selected
//               </Typography>
//             )}
//           </Box>
//         </Box>
//         <Divider sx={{ mb: 2 }} />
//         <Grid container spacing={{ xs: 2, md: 4 }}>
//           {sections.map((section, index) => (
//             <Grid item xs={12} md={4} key={index}>
//               <Section {...section} />
//             </Grid>
//           ))}
//         </Grid>
//       </CardContent>
//     </Card>
//   );
// };

// // AnalogClock component
// const AnalogClock = ({
//   elapsedTimeInSeconds,
//   isShiftActive
// }: {
//   elapsedTimeInSeconds: number;
//   isShiftActive: boolean;
// }) => {
//   const hours = Math.floor(elapsedTimeInSeconds / 3600) % 12;
//   const minutes = Math.floor((elapsedTimeInSeconds % 3600) / 60);
//   const seconds = elapsedTimeInSeconds % 60;

//   const hourDeg = (hours % 12) * 30 + (minutes / 60) * 30;
//   const minuteDeg = (minutes % 60) * 6 + (seconds / 60) * 6;
//   const secondDeg = (seconds % 60) * 6;

//   return (
//     <Tooltip title="Elapsed time of the current shift">
//       <Box
//         sx={{
//           width: { xs: 100, sm: 140 },
//           height: { xs: 100, sm: 140 },
//           bgcolor: '#f5f5f5',
//           borderRadius: '50%',
//           boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
//           position: 'relative',
//           display: 'flex',
//           justifyContent: 'center',
//           alignItems: 'center'
//         }}
//       >
//         <svg
//           width="100%"
//           height="100%"
//           viewBox="0 0 100 100"
//           style={{ position: 'absolute', top: 0, left: 0 }}
//         >
//           <circle cx="50" cy="50" r="45" fill="none" stroke="#e0e0e0" strokeWidth="2" />
//           {[...Array(12)].map((_, i) => (
//             <line
//               key={i}
//               x1="50"
//               y1="10"
//               x2="50"
//               y2="15"
//               stroke="#757575"
//               strokeWidth="2"
//               transform={`rotate(${i * 30} 50 50)`}
//             />
//           ))}
//           <line
//             x1="50"
//             y1="50"
//             x2="50"
//             y2="30"
//             stroke={isShiftActive ? '#4caf50' : '#e0e0e0'}
//             strokeWidth="4"
//             strokeLinecap="round"
//             transform={`rotate(${hourDeg} 50 50)`}
//           />
//           <line
//             x1="50"
//             y1="50"
//             x2="50"
//             y2="20"
//             stroke={isShiftActive ? '#4caf50' : '#e0e0e0'}
//             strokeWidth="3"
//             strokeLinecap="round"
//             transform={`rotate(${minuteDeg} 50 50)`}
//           />
//           <line
//             x1="50"
//             y1="50"
//             x2="50"
//             y2="15"
//             stroke={isShiftActive ? '#f44336' : '#e0e0e0'}
//             strokeWidth="1"
//             strokeLinecap="round"
//             transform={`rotate(${secondDeg} 50 50)`}
//           />
//           <circle cx="50" cy="50" r="3" fill={isShiftActive ? '#4caf50' : '#e0e0e0'} />
//         </svg>
//       </Box>
//     </Tooltip>
//   );
// };

// // TimeTracker component
// const TimeTracker = ({
//   isShiftStarted,
//   elapsedTimeInSeconds,
//   totalTimeWorked,
//   handleClockIn,
//   handleClockOut,
//   formatElapsedTime,
//   clockInLoading,
//   clockOutLoading,
//   totalHoursProjected,
//   isShiftCompleted
// }: {
//   isShiftStarted: boolean;
//   elapsedTimeInSeconds: number;
//   totalTimeWorked: string | null;
//   handleClockIn: () => void;
//   handleClockOut: () => void;
//   formatElapsedTime: (seconds: number) => string;
//   clockInLoading: boolean;
//   clockOutLoading: boolean;
//   totalHoursProjected: string;
//   isShiftCompleted: boolean;
// }) => {
//   const totalSeconds = parseFloat(totalHoursProjected || '8') * 3600;
//   const progress = Math.min((elapsedTimeInSeconds / totalSeconds) * 100, 100);

//   return (
//     <Card sx={{ boxShadow: 3, borderRadius: 2, m: { xs: 1, md: 0 }, p: { xs: 1, md: 2 } }}>
//       <CardContent sx={{ p: { xs: 1, md: 2 } }}>
//         <Typography
//           variant="h6"
//           sx={{
//             fontWeight: 'bold',
//             mb: 2,
//             textAlign: 'center',
//             fontSize: { xs: '1.25rem', md: '1.5rem' }
//           }}
//         >
//           Time Tracker
//         </Typography>
//         {(clockInLoading || clockOutLoading) && (
//           <Box display="flex" justifyContent="center" mb={2}>
//             <CircularProgress size={30} />
//           </Box>
//         )}
//         <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
//           <Tooltip title="Progress of the current shift">
//             <Box position="relative" display="inline-flex" mb={1}>
//               <CircularProgress
//                 variant="determinate"
//                 value={isShiftStarted && !isShiftCompleted ? progress : isShiftCompleted ? 100 : 0}
//                 size={160}
//                 thickness={5}
//                 sx={{
//                   color:
//                     isShiftStarted && !isShiftCompleted
//                       ? '#4caf50'
//                       : isShiftCompleted
//                       ? '#2196f3'
//                       : '#e0e0e0',
//                   width: { xs: 120, sm: 160 },
//                   height: { xs: 120, sm: 160 }
//                 }}
//               />
//               <Box
//                 sx={{
//                   position: 'absolute',
//                   top: '50%',
//                   left: '50%',
//                   transform: 'translate(-50%, -50%)',
//                   textAlign: 'center'
//                 }}
//               >
//                 <AnalogClock
//                   elapsedTimeInSeconds={elapsedTimeInSeconds}
//                   isShiftActive={isShiftStarted && !isShiftCompleted}
//                 />
//               </Box>
//             </Box>
//           </Tooltip>
//           <Typography
//             variant="h6"
//             sx={{
//               fontWeight: 'bold',
//               color: '#333',
//               mt: 1,
//               fontSize: { xs: '1.25rem', sm: '1.5rem' }
//             }}
//           >
//             {formatElapsedTime(elapsedTimeInSeconds)}
//           </Typography>
//         </Box>
//         <Box textAlign="center" mb={2}>
//           {isShiftCompleted ? (
//             <Typography
//               variant="body1"
//               sx={{
//                 fontWeight: 'bold',
//                 color: '#2196f3',
//                 fontSize: { xs: '0.875rem', md: '1rem' }
//               }}
//             >
//               Shift Completed
//             </Typography>
//           ) : !isShiftStarted && !totalTimeWorked ? (
//             <Typography
//               variant="body1"
//               sx={{
//                 fontWeight: 'bold',
//                 color: '#757575',
//                 fontSize: { xs: '0.875rem', md: '1rem' }
//               }}
//             >
//               Shift not started
//             </Typography>
//           ) : isShiftStarted ? (
//             <Typography
//               variant="body1"
//               sx={{
//                 fontWeight: 'bold',
//                 color: '#4caf50',
//                 fontSize: { xs: '0.875rem', md: '1rem' }
//               }}
//             >
//               Shift in progress
//             </Typography>
//           ) : null}
//           {totalTimeWorked && (
//             <Typography
//               variant="body1"
//               sx={{
//                 fontWeight: 'bold',
//                 color: '#333',
//                 mt: 1,
//                 fontSize: { xs: '0.875rem', md: '1rem' }
//               }}
//             >
//               Total Time Worked: {totalTimeWorked}
//             </Typography>
//           )}
//         </Box>
//         {!isShiftCompleted && (
//           <Box
//             sx={{
//               display: 'flex',
//               justifyContent: 'center',
//               gap: { xs: 1, sm: 2 },
//               flexDirection: { xs: 'column', sm: 'row' }
//             }}
//           >
//             <Tooltip title="Start the shift">
//               <Button
//                 variant="contained"
//                 color="primary"
//                 startIcon={<PlayArrowIcon />}
//                 onClick={handleClockIn}
//                 disabled={isShiftStarted || clockInLoading}
//                 sx={{
//                   minWidth: { xs: '100%', sm: 120 },
//                   fontSize: { xs: '0.875rem', md: '1rem' }
//                 }}
//               >
//                 Clock In
//               </Button>
//             </Tooltip>
//             <Tooltip title="End the shift">
//               <Button
//                 variant="contained"
//                 color="secondary"
//                 startIcon={<StopIcon />}
//                 onClick={handleClockOut}
//                 disabled={!isShiftStarted || clockOutLoading}
//                 sx={{
//                   minWidth: { xs: '100%', sm: 120 },
//                   fontSize: { xs: '0.875rem', md: '1rem' }
//                 }}
//               >
//                 Clock Out
//               </Button>
//             </Tooltip>
//           </Box>
//         )}
//       </CardContent>
//     </Card>
//   );
// };

// // EmployeeHistory component
// const EmployeeHistory = ({
//   historyView,
//   setHistoryView
// }: {
//   historyView: 'day' | 'week' | 'month' | 'year';
//   setHistoryView: (view: 'day' | 'week' | 'month' | 'year') => void;
// }) => (
//   <Card sx={{ boxShadow: 3, borderRadius: 2, m: { xs: 1, md: 0 } }}>
//     <CardContent sx={{ p: { xs: 2, md: 3 } }}>
//       <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
//         Employee In/Out History
//       </Typography>
//       <Box mt={2}>
//         <Select
//           value={historyView}
//           onChange={(e) => setHistoryView(e.target.value as 'day' | 'week' | 'month' | 'year')}
//           fullWidth
//           sx={{ mb: 4 }}
//         >
//           <MenuItem value="day">Day</MenuItem>
//           <MenuItem value="week">Week</MenuItem>
//           <MenuItem value="month">Month</MenuItem>
//           <MenuItem value="year">Year</MenuItem>
//         </Select>
//       </Box>
//       <Box mt={4} sx={{ height: { xs: 250, md: 300 } }}>
//         <Bar
//           data={{
//             labels:
//               historyView === 'day'
//                 ? ['9am', '12pm', '3pm', '6pm', '9pm']
//                 : historyView === 'week'
//                 ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']
//                 : historyView === 'month'
//                 ? ['Week 1', 'Week 2', 'Week 3', 'Week 4']
//                 : [
//                     'Jan',
//                     'Feb',
//                     'Mar',
//                     'Apr',
//                     'May',
//                     'Jun',
//                     'Jul',
//                     'Aug',
//                     'Sep',
//                     'Oct',
//                     'Nov',
//                     'Dec'
//                   ],
//             datasets: [
//               {
//                 label: 'Average Clock-In Time',
//                 data:
//                   historyView === 'day'
//                     ? [9, 9.2, 9.1, 9.3, 9.5]
//                     : historyView === 'week'
//                     ? [9, 9.1, 8.9, 9.2, 9]
//                     : historyView === 'month'
//                     ? [9.1, 9.05, 9.2, 9.15]
//                     : [9.05, 9.2, 9.1, 9, 9.15, 9.1, 9.2, 9.1, 9.05, 9.15, 9.1, 9.2],
//                 backgroundColor: 'rgba(63, 81, 181, 0.6)',
//                 borderColor: 'rgba(63, 81, 181, 1)',
//                 borderWidth: 1
//               },
//               {
//                 label: 'Average Clock-Out Time',
//                 data:
//                   historyView === 'day'
//                     ? [18, 18.2, 18.1, 18.3, 18.5]
//                     : historyView === 'week'
//                     ? [18, 18.2, 17.9, 18.3, 18]
//                     : historyView === 'month'
//                     ? [18.1, 18.05, 18.2, 18.15]
//                     : [18.05, 18.2, 18.1, 18, 18.15, 18.1, 18.2, 18.1, 18.05, 18.15, 18.1, 18.2],
//                 backgroundColor: 'rgba(244, 67, 54, 0.6)',
//                 borderColor: 'rgba(244, 67, 54, 1)',
//                 borderWidth: 1
//               }
//             ]
//           }}
//           options={{
//             maintainAspectRatio: false,
//             responsive: true,
//             plugins: { legend: { position: 'top' } }
//           }}
//         />
//       </Box>
//     </CardContent>
//   </Card>
// );

// // Main ClockInOut component
// const ClockInOut: React.FC = () => {
//   const dispatch = useDispatch<AppDispatch>();
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

//   const location = useLocation();
//   const { selectedClient, pathname } = location.state || {};
//   const selectedProjectId = useAppSelector((state) => state.homeTilesServices.selectedProjectId);
//   const selectedServiceTitle = useAppSelector(
//     (state) => state.serviceSelected.selectedServiceTitle
//   );

//   const componentParentId = pathname;
//   const userDetailsString = localStorage.getItem('user-details');
//   const userDetails = JSON.parse(userDetailsString || '{}');

//   const basePayload = useMemo(
//     () => ({
//       group_id: selectedProjectId || '',
//       service_name_id: selectedServiceTitle || '',
//       // component_parent_id: componentParentId,
//       // nested_component_id: 'clockInOut',
//       component_parent_id: '/profile/weekly-patients-schedule',
//       nested_component_id: 'weekly-patients-schedule',
//       user_whoRequested_id: selectedClient?.label || '',
//       loggedIN_user_id: userDetails?.name || '',
//       loggedIN_user_email: userDetails?.email || ''
//     }),
//     [selectedProjectId, selectedServiceTitle, componentParentId, selectedClient, userDetails]
//   );

//   const { shifts, loading, error } = useSelector((state: RootState) => state.clockInOut);
//   const [selectedShift, setSelectedShift] = useState<Shift | null>(null);
//   const [upcomingShift, setUpcomingShift] = useState<Shift | null>(null);
//   const [isShiftStarted, setIsShiftStarted] = useState(false);
//   const [elapsedTimeInSeconds, setElapsedTimeInSeconds] = useState(0);
//   const [totalTimeWorked, setTotalTimeWorked] = useState<string | null>(null);
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [isShiftNotesModalOpen, setIsShiftNotesModalOpen] = useState(false);
//   const [historyView, setHistoryView] = useState<'day' | 'week' | 'month' | 'year'>('day');
//   const [clockInLoading, setClockInLoading] = useState(false);
//   const [clockOutLoading, setClockOutLoading] = useState(false);
//   const [snackbar, setSnackbar] = useState<{
//     open: boolean;
//     message: string;
//     severity: 'success' | 'error';
//   }>({
//     open: false,
//     message: '',
//     severity: 'success'
//   });
//   const [clockInTime, setClockInTime] = useState<string | null>(null);
//   const navigate = useNavigate();

//   // Format date from ISO timestamp
//   const formatDate = (isoTimestamp: string | undefined): string => {
//     if (!isoTimestamp) return '';
//     try {
//       const normalized = normalizeTimestamp(isoTimestamp);
//       return format(parseISO(normalized!), 'dd/MM/yy');
//     } catch {
//       return 'Invalid date';
//     }
//   };

//   // Fetch shifts from API
//   useEffect(() => {
//     dispatch(fetchShifts(basePayload));
//   }, [dispatch]);

//   // Set default and upcoming shifts
//   useEffect(() => {
//     if (shifts.length > 0 && !selectedShift) {
//       const today = format(new Date(), 'yyyy-MM-dd');
//       const defaultShift =
//         shifts.find((shift) => {
//           try {
//             const normalizedStart = normalizeTimestamp(shift.start);
//             return (
//               !shift.actual_end &&
//               shift.start &&
//               format(parseISO(normalizedStart!), 'yyyy-MM-dd') === today
//             );
//           } catch {
//             return false;
//           }
//         }) || shifts[0];
//       const otherShift = shifts.find((shift) => shift.id !== defaultShift?.id) || null;
//       setSelectedShift(defaultShift || null);
//       setUpcomingShift(otherShift);
//       setIsShiftStarted(!!defaultShift?.actual_start && !defaultShift?.actual_end);
//       setClockInTime(defaultShift?.actual_start || null);
//       setTotalTimeWorked(
//         defaultShift?.actual_worked_hours && defaultShift?.actual_end
//           ? `${defaultShift.actual_worked_hours} hours`
//           : null
//       );

//       // Calculate elapsed time for started or completed shifts
//       if (defaultShift?.actual_start) {
//         try {
//           const normalizedStart = normalizeTimestamp(defaultShift.actual_start);
//           const startTime = parseISO(normalizedStart!);
//           const endTime = defaultShift.actual_end
//             ? parseISO(normalizeTimestamp(defaultShift.actual_end)!)
//             : new Date();
//           const elapsed = differenceInSeconds(endTime, startTime);
//           setElapsedTimeInSeconds(elapsed > 0 ? elapsed : 0);
//         } catch {
//           setElapsedTimeInSeconds(0);
//         }
//       }
//     }
//   }, [shifts, selectedShift]);

//   // Timer for elapsed time
//   useEffect(() => {
//     let interval: NodeJS.Timeout | null = null;
//     if (isShiftStarted && clockInTime && !selectedShift?.actual_end) {
//       interval = setInterval(() => {
//         try {
//           const normalizedStart = normalizeTimestamp(clockInTime);
//           const startTime = parseISO(normalizedStart!);
//           const currentTime = new Date();
//           const elapsed = differenceInSeconds(currentTime, startTime);
//           setElapsedTimeInSeconds(elapsed > 0 ? elapsed : 0);
//         } catch {
//           setElapsedTimeInSeconds(0);
//         }
//       }, 1000);
//     }
//     return () => {
//       if (interval) clearInterval(interval);
//     };
//   }, [isShiftStarted, clockInTime, selectedShift?.actual_end]);

//   const handleOpenModal = () => setIsModalOpen(true);
//   const handleCloseModal = () => setIsModalOpen(false);

//   const handleClockIn = async () => {
//     if (!selectedShift) {
//       setSnackbar({ open: true, message: 'No shift selected', severity: 'error' });
//       return;
//     }

//     // Check if current time is before shift start
//     try {
//       const normalizedStart = normalizeTimestamp(selectedShift.start);
//       const shiftStart = parseISO(normalizedStart!);
//       const currentTime = new Date();
//       if (currentTime < shiftStart) {
//         setSnackbar({
//           open: true,
//           message: 'Cannot clock in before shift start time',
//           severity: 'error'
//         });
//         return;
//       }
//     } catch {
//       setSnackbar({ open: true, message: 'Invalid shift start time', severity: 'error' });
//       return;
//     }

//     setClockInLoading(true);
//     try {
//       const clockInTime = new Date().toISOString();
//       const updatedShift: Shift = {
//         ...selectedShift,
//         actual_start: clockInTime,
//         actual_worked_hours: '0',
//         clockInTime
//       };

//       const clockInPayload: ClockInOutPayload = {
//         ...basePayload,
//         shiftId: selectedShift.id,
//         userId: basePayload.loggedIN_user_id,
//         clockInTime,
//         actual_start: clockInTime,
//         actual_worked_hours: '0',
//         event_id: selectedShift.id,
//         title: selectedShift.title,
//         description: selectedShift.description || '',
//         start: selectedShift.start,
//         end: selectedShift.end,
//         support_item_number: selectedShift.support_item_number || '',
//         support_worder: selectedShift.support_worder || '',
//         manager: selectedShift.manager || '',
//         client_name: selectedShift.client_name || '',
//         color: selectedShift.color || '#2196f3',
//         textColor: selectedShift.textColor || '',
//         allDay: selectedShift.allDay || false
//       };

//       await dispatch(
//         clockIn({
//           shift: selectedShift,
//           clockInTime,
//           payload: clockInPayload
//         })
//       ).unwrap();

//       setIsShiftStarted(true);
//       setClockInTime(clockInTime);
//       setElapsedTimeInSeconds(0);
//       setTotalTimeWorked(null);
//       setSelectedShift(updatedShift);
//       setSnackbar({ open: true, message: 'Successfully clocked in!', severity: 'success' });
//     } catch (error: any) {
//       setSnackbar({
//         open: true,
//         message: error.message || 'Failed to clock in',
//         severity: 'error'
//       });
//     } finally {
//       setClockInLoading(false);
//     }
//   };

//   const handleClockOut = async () => {
//     if (!selectedShift || !clockInTime) {
//       setSnackbar({
//         open: true,
//         message: 'Shift not started or no shift selected',
//         severity: 'error'
//       });
//       return;
//     }

//     // Check if current time is before shift end
//     try {
//       const normalizedEnd = normalizeTimestamp(selectedShift.end);
//       const shiftEnd = parseISO(normalizedEnd!);
//       const currentTime = new Date();
//       if (currentTime < shiftEnd) {
//         setSnackbar({
//           open: true,
//           message: 'Cannot clock out before shift end time',
//           severity: 'error'
//         });
//         return;
//       }
//     } catch {
//       setSnackbar({ open: true, message: 'Invalid shift end time', severity: 'error' });
//       return;
//     }

//     setClockOutLoading(true);
//     try {
//       const clockOutTime = new Date().toISOString();
//       const diffInHours = (
//         (new Date(clockOutTime).getTime() - new Date(clockInTime).getTime()) /
//         (1000 * 60 * 60)
//       ).toFixed(2);
//       const diffInSeconds = differenceInSeconds(new Date(clockOutTime), new Date(clockInTime));
//       const updatedShift: Shift = {
//         ...selectedShift,
//         actual_end: clockOutTime,
//         actual_worked_hours: diffInHours,
//         clockOutTime
//       };

//       const clockOutPayload: ClockInOutPayload = {
//         ...basePayload,
//         shiftId: selectedShift.id,
//         userId: basePayload.loggedIN_user_id,
//         clockOutTime,
//         actual_end: clockOutTime,
//         actual_worked_hours: diffInHours,
//         event_id: selectedShift.id,
//         title: selectedShift.title,
//         description: selectedShift.description || '',
//         start: selectedShift.start,
//         end: selectedShift.end,
//         support_item_number: selectedShift.support_item_number || '',
//         support_worder: selectedShift.support_worder || '',
//         manager: selectedShift.manager || '',
//         client_name: selectedShift.client_name || '',
//         color: selectedShift.color || '#2196f3',
//         textColor: selectedShift.textColor || '',
//         allDay: selectedShift.allDay || false
//       };

//       await dispatch(
//         clockOut({
//           shift: selectedShift,
//           clockOutTime,
//           clockInTime,
//           payload: clockOutPayload
//         })
//       ).unwrap();

//       setIsShiftStarted(false);
//       setTotalTimeWorked(`${diffInHours} hours`);
//       setClockInTime(null);
//       setElapsedTimeInSeconds(diffInSeconds > 0 ? diffInSeconds : 0);
//       setSelectedShift(updatedShift);
//       setSnackbar({ open: true, message: 'Successfully clocked out!', severity: 'success' });
//       setIsShiftNotesModalOpen(true);
//     } catch (error: any) {
//       setSnackbar({
//         open: true,
//         message: error.message || 'Failed to clock out',
//         severity: 'error'
//       });
//     } finally {
//       setClockOutLoading(false);
//     }
//   };

//   const handleShiftNotesModalClose = () => {
//     setIsShiftNotesModalOpen(false);
//   };

//   const handleFillShiftNotes = () => {
//     setIsShiftNotesModalOpen(false);
//     navigate('/profile/ShiftNotes', { state: { selectedShift } });
//   };

//   const formatElapsedTime = (seconds: number) => {
//     const hours = Math.floor(seconds / 3600);
//     const minutes = Math.floor((seconds % 3600) / 60);
//     return `${hours}h : ${minutes}m`;
//   };

//   const handleSnackbarClose = () => {
//     setSnackbar({ ...snackbar, open: false });
//     dispatch(resetError());
//   };

//   const handleShiftChange = (event: any) => {
//     const shiftId = event.target.value;
//     const shift = shifts.find((s) => s.id === shiftId) || null;
//     const otherShift = shifts.find((s) => s.id !== shiftId) || null;
//     setSelectedShift(shift);
//     setUpcomingShift(otherShift);
//     setIsShiftStarted(!!shift?.actual_start && !shift?.actual_end);
//     setClockInTime(shift?.actual_start || null);
//     setTotalTimeWorked(
//       shift?.actual_worked_hours && shift?.actual_end ? `${shift.actual_worked_hours} hours` : null
//     );

//     // Calculate elapsed time for the selected shift
//     if (shift?.actual_start) {
//       try {
//         const normalizedStart = normalizeTimestamp(shift.actual_start);
//         const startTime = parseISO(normalizedStart!);
//         const endTime = shift.actual_end
//           ? parseISO(normalizeTimestamp(shift.actual_end)!)
//           : new Date();
//         const elapsed = differenceInSeconds(endTime, startTime);
//         setElapsedTimeInSeconds(elapsed > 0 ? elapsed : 0);
//       } catch {
//         setElapsedTimeInSeconds(0);
//       }
//     } else {
//       setElapsedTimeInSeconds(0);
//     }
//   };

//   // Calculate total hours projected from start and end times
//   const calculateTotalHoursProjected = (shift: Shift | null): string => {
//     if (!shift?.start || !shift?.end) return '8';
//     try {
//       const normalizedStart = normalizeTimestamp(shift.start);
//       const normalizedEnd = normalizeTimestamp(shift.end);
//       const start = parseISO(normalizedStart!);
//       const end = parseISO(normalizedEnd!);
//       const diffInMs = end.getTime() - start.getTime();
//       return (diffInMs / (1000 * 60 * 60)).toFixed(2);
//     } catch {
//       return '8';
//     }
//   };

//   return (
//     <Box sx={{ minHeight: '100vh', p: { xs: 1, md: 3 } }}>
//       <Grid container spacing={{ xs: 1, md: 3 }}>
//         <Grid item xs={12}>
//           <Box mb={2}>
//             <FormControl fullWidth>
//               <InputLabel>Select Shift</InputLabel>
//               <Select
//                 value={selectedShift?.id || ''}
//                 onChange={handleShiftChange}
//                 label="Select Shift"
//                 disabled={loading}
//               >
//                 {shifts.length === 0 && (
//                   <MenuItem value="" disabled>
//                     No shifts available
//                   </MenuItem>
//                 )}
//                 {shifts.map((shift) => (
//                   <MenuItem key={shift.id} value={shift.id}>
//                     {shift.title} ({shift.start ? formatDate(shift.start) : 'N/A'})
//                   </MenuItem>
//                 ))}
//               </Select>
//             </FormControl>
//           </Box>
//           {loading && (
//             <Box display="flex" justifyContent="center" my={2}>
//               <CircularProgress />
//             </Box>
//           )}
//           <JobDetails
//             shift={selectedShift}
//             upcomingShift={upcomingShift}
//             handleOpenModal={handleOpenModal}
//             formatDate={formatDate}
//           />
//         </Grid>
//         <Grid item xs={12} md={6}>
//           <TimeTracker
//             isShiftStarted={isShiftStarted}
//             elapsedTimeInSeconds={elapsedTimeInSeconds}
//             totalTimeWorked={totalTimeWorked}
//             handleClockIn={handleClockIn}
//             handleClockOut={handleClockOut}
//             formatElapsedTime={formatElapsedTime}
//             clockInLoading={clockInLoading}
//             clockOutLoading={clockOutLoading}
//             totalHoursProjected={calculateTotalHoursProjected(selectedShift)}
//             isShiftCompleted={!!selectedShift?.actual_end}
//           />
//         </Grid>
//         <Grid item xs={12} md={6}>
//           <OtherDetails />
//         </Grid>
//         <Grid item xs={12}>
//           <EmployeeHistory historyView={historyView} setHistoryView={setHistoryView} />
//         </Grid>
//       </Grid>
//       <Dialog
//         open={isModalOpen}
//         onClose={handleCloseModal}
//         maxWidth="lg"
//         fullWidth
//         fullScreen={isMobile}
//       >
//         <DialogTitle>
//           <IconButton
//             aria-label="close"
//             onClick={handleCloseModal}
//             sx={{
//               position: 'absolute',
//               right: 16,
//               top: 16,
//               color: (theme) => theme.palette.grey[500],
//               borderRadius: '50%'
//             }}
//           >
//             <CloseIcon />
//           </IconButton>
//         </DialogTitle>
//         <DialogContent sx={{ mt: 2, p: { xs: 2, md: 3 } }}>
//           <ShiftDetailsTable />
//         </DialogContent>
//       </Dialog>
//       <Dialog
//         open={isShiftNotesModalOpen}
//         onClose={handleShiftNotesModalClose}
//         maxWidth="sm"
//         fullWidth
//       >
//         <DialogTitle>Shift Notes</DialogTitle>
//         <DialogContent>
//           <Typography variant="body1">
//             You have successfully clocked out. Would you like to fill out shift notes now?
//           </Typography>
//         </DialogContent>
//         <DialogActions>
//           <Button onClick={handleShiftNotesModalClose} color="primary">
//             No, Later
//           </Button>
//           <Button onClick={handleFillShiftNotes} color="primary" variant="contained">
//             Yes, Fill Now
//           </Button>
//         </DialogActions>
//       </Dialog>
//       ;
//       <Snackbar
//         open={snackbar.open || !!error}
//         autoHideDuration={6000}
//         onClose={handleSnackbarClose}
//         anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
//       >
//         <Alert
//           onClose={handleSnackbarClose}
//           severity={error ? 'error' : snackbar.severity}
//           sx={{ width: '100%' }}
//         >
//           {error || snackbar.message}
//         </Alert>
//       </Snackbar>
//     </Box>
//   );
// };

// export default ClockInOut;


































































// import {
//   Alert,
//   Box,
//   Button,
//   Card,
//   CardContent,
//   CircularProgress,
//   Dialog,
//   DialogActions,
//   DialogContent,
//   DialogTitle,
//   Divider,
//   FormControl,
//   Grid,
//   IconButton,
//   InputLabel,
//   MenuItem,
//   Select,
//   Snackbar,
//   SvgIconProps,
//   Tooltip,
//   Typography,
//   useMediaQuery,
//   useTheme
// } from '@mui/material';
// import AlarmIcon from '@mui/icons-material/Alarm';
// import CloseIcon from '@mui/icons-material/Close';
// import EventIcon from '@mui/icons-material/Event';
// import PlayArrowIcon from '@mui/icons-material/PlayArrow';
// import StopIcon from '@mui/icons-material/Stop';
// import TableViewIcon from '@mui/icons-material/TableView';
// import WorkIcon from '@mui/icons-material/Work';
// import { useAppSelector } from 'app/hooks';
// import 'chart.js/auto';
// import { format, parseISO, differenceInSeconds } from 'date-fns';
// import React, { useEffect, useMemo, useState } from 'react';
// import { Bar } from 'react-chartjs-2';
// import { useDispatch, useSelector } from 'react-redux';
// import { useLocation, useNavigate } from 'react-router-dom';
// import { AppDispatch, RootState } from 'store';
// import { ClockInOutPayload, Shift } from 'types/ndisSection/clockInOutTypes';
// import {
//   clockIn,
//   clockOut,
//   fetchShifts,
//   resetError
// } from '../../../slices/NdisSlices/clockInOut/ClockInOutSlice';
// import ShiftDetailsTable from '../shiftDetails/ShiftDetailsTable';
// import OtherDetails from './OtherDetails';

// // Utility function to normalize timestamps
// const normalizeTimestamp = (timestamp: string | undefined): string | undefined => {
//   if (!timestamp) return undefined;
//   return timestamp.replace(/T(\d{2})_(\d{2})_(\d{2})/, 'T$1:$2:$3');
// };

// // Section component
// interface SectionProps {
//   title: string;
//   icon: React.ComponentType<SvgIconProps>;
//   color: string;
//   items: { label?: string; value: string }[];
// }

// const Section: React.FC<SectionProps> = ({ title, icon: Icon, color, items }) => (
//   <Box>
//     <Typography
//       variant="h6"
//       sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', mb: 2, color }}
//     >
//       <Icon sx={{ mr: 1 }} />
//       {title}
//     </Typography>
//     <Divider sx={{ mb: 2 }} />
//     {items.map((item, index) => (
//       <Box sx={{ display: 'flex', mb: index !== items.length - 1 ? 1 : 0 }} key={index}>
//         {item.label && (
//           <Typography
//             variant="body2"
//             sx={{ color: '#757575', fontWeight: 'bold', flex: '0 0 150px' }}
//           >
//             {item.label}:
//           </Typography>
//         )}
//         <Typography variant="body1">{item.value}</Typography>
//       </Box>
//     ))}
//   </Box>
// );

// // JobDetails component
// const JobDetails = ({
//   shift,
//   upcomingShift,
//   handleOpenModal,
//   formatDate
// }: {
//   shift: Shift | null;
//   upcomingShift: Shift | null;
//   handleOpenModal: () => void;
//   formatDate: (isoTimestamp: string | undefined) => string;
// }) => {
//   const formatISOTimestamp = (isoTimestamp: string | undefined): string => {
//     if (!isoTimestamp) return '';
//     try {
//       const normalized = normalizeTimestamp(isoTimestamp);
//       return format(parseISO(normalized!), 'dd/MM/yy, hh:mm a');
//     } catch {
//       return 'Invalid time';
//     }
//   };

//   const calculateTotalHoursProjected = (shift: Shift | null): string => {
//     if (!shift?.start || !shift?.end) return '0';
//     try {
//       const normalizedStart = normalizeTimestamp(shift.start);
//       const normalizedEnd = normalizeTimestamp(shift.end);
//       const start = parseISO(normalizedStart!);
//       const end = parseISO(normalizedEnd!);
//       const diffInMs = end.getTime() - start.getTime();
//       return (diffInMs / (1000 * 60 * 60)).toFixed(2);
//     } catch {
//       return '0';
//     }
//   };

//   const sections = [
//     {
//       title: 'Work Updates',
//       icon: WorkIcon,
//       color: '#4caf50',
//       items: [
//         { label: 'Client Name', value: shift?.client_name || 'N/A' },
//         {
//           label: 'Planned Start',
//           value: shift?.start ? formatISOTimestamp(shift.start) : ''
//         },
//         {
//           label: 'Planned End',
//           value: shift?.end ? formatISOTimestamp(shift.end) : ''
//         },
//         {
//           label: 'Actual Start',
//           value: shift?.actual_start ? formatISOTimestamp(shift.actual_start) : 'Not started'
//         },
//         {
//           label: 'Actual End',
//           value: shift?.actual_end ? formatISOTimestamp(shift.actual_end) : 'Not completed'
//         },
//         {
//           label: 'Planned Hours',
//           value: shift ? `${calculateTotalHoursProjected(shift)} hours` : '0 hours'
//         },
//         {
//           label: 'Hours Worked',
//           value: shift?.actual_worked_hours ? `${shift.actual_worked_hours} hours` : '0 hours'
//         },
//         { label: 'Date', value: shift?.start ? formatDate(shift.start) : '' }
//       ]
//     },
//     {
//       title: 'Upcoming Shift',
//       icon: AlarmIcon,
//       color: '#ff9800',
//       items: [
//         { label: 'Client Name', value: upcomingShift?.client_name || 'N/A' },
//         {
//           label: 'Start Time',
//           value: upcomingShift?.start ? formatISOTimestamp(upcomingShift.start) : ''
//         },
//         {
//           label: 'End Time',
//           value: upcomingShift?.end ? formatISOTimestamp(upcomingShift.end) : ''
//         },
//         { label: 'Date', value: upcomingShift?.start ? formatDate(upcomingShift.start) : '' },
//         {
//           label: 'Total hours projected',
//           value: upcomingShift ? `${calculateTotalHoursProjected(upcomingShift)} hours` : '0 hours'
//         }
//       ]
//     },
//     {
//       title: 'Planned Leave',
//       icon: EventIcon,
//       color: '#9c27b0',
//       items: [
//         { label: 'Date', value: '' },
//         { label: 'Start Time', value: '' },
//         { label: 'End Time', value: '' },
//         { label: 'Total hours projected', value: '' }
//       ]
//     }
//   ];

//   return (
//     <Card
//       sx={{ boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)', borderRadius: 4, m: { xs: 1, md: 0 } }}
//     >
//       <CardContent sx={{ p: { xs: 2, md: 3 } }}>
//         <Box
//           display="flex"
//           justifyContent="space-between"
//           alignItems="center"
//           flexWrap="wrap"
//           mb={2}
//         >
//           <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
//             Job Details
//           </Typography>
//           <Box>
//             {shift ? (
//               <IconButton color="primary" onClick={handleOpenModal} sx={{ mt: 1 }}>
//                 <TableViewIcon />
//               </IconButton>
//             ) : (
//               <Typography variant="body1" sx={{ color: '#757575', mt: 1 }}>
//                 No shift selected
//               </Typography>
//             )}
//           </Box>
//         </Box>
//         <Divider sx={{ mb: 2 }} />
//         <Grid container spacing={{ xs: 2, md: 4 }}>
//           {sections.map((section, index) => (
//             <Grid item xs={12} md={4} key={index}>
//               <Section {...section} />
//             </Grid>
//           ))}
//         </Grid>
//       </CardContent>
//     </Card>
//   );
// };

// // AnalogClock component
// const AnalogClock = ({
//   elapsedTimeInSeconds,
//   isShiftActive
// }: {
//   elapsedTimeInSeconds: number;
//   isShiftActive: boolean;
// }) => {
//   const hours = Math.floor(elapsedTimeInSeconds / 3600) % 12;
//   const minutes = Math.floor((elapsedTimeInSeconds % 3600) / 60);
//   const seconds = elapsedTimeInSeconds % 60;

//   const hourDeg = (hours % 12) * 30 + (minutes / 60) * 30;
//   const minuteDeg = (minutes % 60) * 6 + (seconds / 60) * 6;
//   const secondDeg = (seconds % 60) * 6;

//   return (
//     <Tooltip title="Elapsed time of the current shift">
//       <Box
//         sx={{
//           width: { xs: 100, sm: 140 },
//           height: { xs: 100, sm: 140 },
//           bgcolor: '#f5f5f5',
//           borderRadius: '50%',
//           boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
//           position: 'relative',
//           display: 'flex',
//           justifyContent: 'center',
//           alignItems: 'center'
//         }}
//       >
//         <svg
//           width="100%"
//           height="100%"
//           viewBox="0 0 100 100"
//           style={{ position: 'absolute', top: 0, left: 0 }}
//         >
//           <circle cx="50" cy="50" r="45" fill="none" stroke="#e0e0e0" strokeWidth="2" />
//           {[...Array(12)].map((_, i) => (
//             <line
//               key={i}
//               x1="50"
//               y1="10"
//               x2="50"
//               y2="15"
//               stroke="#757575"
//               strokeWidth="2"
//               transform={`rotate(${i * 30} 50 50)`}
//             />
//           ))}
//           <line
//             x1="50"
//             y1="50"
//             x2="50"
//             y2="30"
//             stroke={isShiftActive ? '#4caf50' : '#e0e0e0'}
//             strokeWidth="4"
//             strokeLinecap="round"
//             transform={`rotate(${hourDeg} 50 50)`}
//           />
//           <line
//             x1="50"
//             y1="50"
//             x2="50"
//             y2="20"
//             stroke={isShiftActive ? '#4caf50' : '#e0e0e0'}
//             strokeWidth="3"
//             strokeLinecap="round"
//             transform={`rotate(${minuteDeg} 50 50)`}
//           />
//           <line
//             x1="50"
//             y1="50"
//             x2="50"
//             y2="15"
//             stroke={isShiftActive ? '#f44336' : '#e0e0e0'}
//             strokeWidth="1"
//             strokeLinecap="round"
//             transform={`rotate(${secondDeg} 50 50)`}
//           />
//           <circle cx="50" cy="50" r="3" fill={isShiftActive ? '#4caf50' : '#e0e0e0'} />
//         </svg>
//       </Box>
//     </Tooltip>
//   );
// };

// // TimeTracker component
// const TimeTracker = ({
//   isShiftStarted,
//   elapsedTimeInSeconds,
//   totalTimeWorked,
//   handleClockIn,
//   handleClockOut,
//   formatElapsedTime,
//   clockInLoading,
//   clockOutLoading,
//   totalHoursProjected,
//   isShiftCompleted
// }: {
//   isShiftStarted: boolean;
//   elapsedTimeInSeconds: number;
//   totalTimeWorked: string | null;
//   handleClockIn: () => void;
//   handleClockOut: () => void;
//   formatElapsedTime: (seconds: number) => string;
//   clockInLoading: boolean;
//   clockOutLoading: boolean;
//   totalHoursProjected: string;
//   isShiftCompleted: boolean;
// }) => {
//   const totalSeconds = parseFloat(totalHoursProjected || '8') * 3600;
//   const progress = Math.min((elapsedTimeInSeconds / totalSeconds) * 100, 100);

//   return (
//     <Card sx={{ boxShadow: 3, borderRadius: 2, m: { xs: 1, md: 0 }, p: { xs: 1, md: 2 } }}>
//       <CardContent sx={{ p: { xs: 1, md: 2 } }}>
//         <Typography
//           variant="h6"
//           sx={{
//             fontWeight: 'bold',
//             mb: 2,
//             textAlign: 'center',
//             fontSize: { xs: '1.25rem', md: '1.5rem' }
//           }}
//         >
//           Time Tracker
//         </Typography>
//         {(clockInLoading || clockOutLoading) && (
//           <Box display="flex" justifyContent="center" mb={2}>
//             <CircularProgress size={30} />
//           </Box>
//         )}
//         <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
//           <Tooltip title="Progress of the current shift">
//             <Box position="relative" display="inline-flex" mb={1}>
//               <CircularProgress
//                 variant="determinate"
//                 value={isShiftStarted && !isShiftCompleted ? progress : isShiftCompleted ? 100 : 0}
//                 size={160}
//                 thickness={5}
//                 sx={{
//                   color:
//                     isShiftStarted && !isShiftCompleted
//                       ? '#4caf50'
//                       : isShiftCompleted
//                       ? '#2196f3'
//                       : '#e0e0e0',
//                   width: { xs: 120, sm: 160 },
//                   height: { xs: 120, sm: 160 }
//                 }}
//               />
//               <Box
//                 sx={{
//                   position: 'absolute',
//                   top: '50%',
//                   left: '50%',
//                   transform: 'translate(-50%, -50%)',
//                   textAlign: 'center'
//                 }}
//               >
//                 <AnalogClock
//                   elapsedTimeInSeconds={elapsedTimeInSeconds}
//                   isShiftActive={isShiftStarted && !isShiftCompleted}
//                 />
//               </Box>
//             </Box>
//           </Tooltip>
//           <Typography
//             variant="h6"
//             sx={{
//               fontWeight: 'bold',
//               color: '#333',
//               mt: 1,
//               fontSize: { xs: '1.25rem', sm: '1.5rem' }
//             }}
//           >
//             {formatElapsedTime(elapsedTimeInSeconds)}
//           </Typography>
//         </Box>
//         <Box textAlign="center" mb={2}>
//           {isShiftCompleted ? (
//             <Typography
//               variant="body1"
//               sx={{
//                 fontWeight: 'bold',
//                 color: '#2196f3',
//                 fontSize: { xs: '0.875rem', md: '1rem' }
//               }}
//             >
//               Shift Completed
//             </Typography>
//           ) : !isShiftStarted && !totalTimeWorked ? (
//             <Typography
//               variant="body1"
//               sx={{
//                 fontWeight: 'bold',
//                 color: '#757575',
//                 fontSize: { xs: '0.875rem', md: '1rem' }
//               }}
//             >
//               Shift not started
//             </Typography>
//           ) : isShiftStarted ? (
//             <Typography
//               variant="body1"
//               sx={{
//                 fontWeight: 'bold',
//                 color: '#4caf50',
//                 fontSize: { xs: '0.875rem', md: '1rem' }
//               }}
//             >
//               Shift in progress
//             </Typography>
//           ) : null}
//           {totalTimeWorked && (
//             <Typography
//               variant="body1"
//               sx={{
//                 fontWeight: 'bold',
//                 color: '#333',
//                 mt: 1,
//                 fontSize: { xs: '0.875rem', md: '1rem' }
//               }}
//             >
//               Total Time Worked: {totalTimeWorked}
//             </Typography>
//           )}
//         </Box>
//         {!isShiftCompleted && (
//           <Box
//             sx={{
//               display: 'flex',
//               justifyContent: 'center',
//               gap: { xs: 1, sm: 2 },
//               flexDirection: { xs: 'column', sm: 'row' }
//             }}
//           >
//             <Tooltip title="Start the shift">
//               <Button
//                 variant="contained"
//                 color="primary"
//                 startIcon={<PlayArrowIcon />}
//                 onClick={handleClockIn}
//                 disabled={isShiftStarted || clockInLoading}
//                 sx={{
//                   minWidth: { xs: '100%', sm: 120 },
//                   fontSize: { xs: '0.875rem', md: '1rem' }
//                 }}
//               >
//                 Clock In
//               </Button>
//             </Tooltip>
//             <Tooltip title="End the shift">
//               <Button
//                 variant="contained"
//                 color="secondary"
//                 startIcon={<StopIcon />}
//                 onClick={handleClockOut}
//                 disabled={!isShiftStarted || clockOutLoading}
//                 sx={{
//                   minWidth: { xs: '100%', sm: 120 },
//                   fontSize: { xs: '0.875rem', md: '1rem' }
//                 }}
//               >
//                 Clock Out
//               </Button>
//             </Tooltip>
//           </Box>
//         )}
//       </CardContent>
//     </Card>
//   );
// };

// // EmployeeHistory component
// const EmployeeHistory = ({
//   historyView,
//   setHistoryView
// }: {
//   historyView: 'day' | 'week' | 'month' | 'year';
//   setHistoryView: (view: 'day' | 'week' | 'month' | 'year') => void;
// }) => (
//   <Card sx={{ boxShadow: 3, borderRadius: 2, m: { xs: 1, md: 0 } }}>
//     <CardContent sx={{ p: { xs: 2, md: 3 } }}>
//       <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
//         Employee In/Out History
//       </Typography>
//       <Box mt={2}>
//         <Select
//           value={historyView}
//           onChange={(e) => setHistoryView(e.target.value as 'day' | 'week' | 'month' | 'year')}
//           fullWidth
//           sx={{ mb: 4 }}
//         >
//           <MenuItem value="day">Day</MenuItem>
//           <MenuItem value="week">Week</MenuItem>
//           <MenuItem value="month">Month</MenuItem>
//           <MenuItem value="year">Year</MenuItem>
//         </Select>
//       </Box>
//       <Box mt={4} sx={{ height: { xs: 250, md: 300 } }}>
//         <Bar
//           data={{
//             labels:
//               historyView === 'day'
//                 ? ['9am', '12pm', '3pm', '6pm', '9pm']
//                 : historyView === 'week'
//                 ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']
//                 : historyView === 'month'
//                 ? ['Week 1', 'Week 2', 'Week 3', 'Week 4']
//                 : [
//                     'Jan',
//                     'Feb',
//                     'Mar',
//                     'Apr',
//                     'May',
//                     'Jun',
//                     'Jul',
//                     'Aug',
//                     'Sep',
//                     'Oct',
//                     'Nov',
//                     'Dec'
//                   ],
//             datasets: [
//               {
//                 label: 'Average Clock-In Time',
//                 data:
//                   historyView === 'day'
//                     ? [9, 9.2, 9.1, 9.3, 9.5]
//                     : historyView === 'week'
//                     ? [9, 9.1, 8.9, 9.2, 9]
//                     : historyView === 'month'
//                     ? [9.1, 9.05, 9.2, 9.15]
//                     : [9.05, 9.2, 9.1, 9, 9.15, 9.1, 9.2, 9.1, 9.05, 9.15, 9.1, 9.2],
//                 backgroundColor: 'rgba(63, 81, 181, 0.6)',
//                 borderColor: 'rgba(63, 81, 181, 1)',
//                 borderWidth: 1
//               },
//               {
//                 label: 'Average Clock-Out Time',
//                 data:
//                   historyView === 'day'
//                     ? [18, 18.2, 18.1, 18.3, 18.5]
//                     : historyView === 'week'
//                     ? [18, 18.2, 17.9, 18.3, 18]
//                     : historyView === 'month'
//                     ? [18.1, 18.05, 18.2, 18.15]
//                     : [18.05, 18.2, 18.1, 18, 18.15, 18.1, 18.2, 18.1, 18.05, 18.15, 18.1, 18.2],
//                 backgroundColor: 'rgba(244, 67, 54, 0.6)',
//                 borderColor: 'rgba(244, 67, 54, 1)',
//                 borderWidth: 1
//               }
//             ]
//           }}
//           options={{
//             maintainAspectRatio: false,
//             responsive: true,
//             plugins: { legend: { position: 'top' } }
//           }}
//         />
//       </Box>
//     </CardContent>
//   </Card>
// );

// // Main ClockInOut component
// const ClockInOut: React.FC = () => {
//   const dispatch = useDispatch<AppDispatch>();
//   const navigate = useNavigate();
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

//   const location = useLocation();
//   const { selectedClient, pathname } = location.state || {};
//   const selectedProjectId = useAppSelector((state) => state.homeTilesServices.selectedProjectId);
//   const selectedServiceTitle = useAppSelector(
//     (state) => state.serviceSelected.selectedServiceTitle
//   );

//   const componentParentId = pathname;
//   const userDetailsString = localStorage.getItem('user-details');
//   const userDetails = JSON.parse(userDetailsString || '{}');

//   const basePayload = useMemo(
//     () => ({
//       group_id: selectedProjectId || '',
//       service_name_id: selectedServiceTitle || '',
//       component_parent_id: '/profile/weekly-patients-schedule',
//       nested_component_id: 'weekly-patients-schedule',
//       user_whoRequested_id: selectedClient?.label || '',
//       loggedIN_user_id: userDetails?.name || '',
//       loggedIN_user_email: userDetails?.email || ''
//     }),
//     [selectedProjectId, selectedServiceTitle, componentParentId, selectedClient, userDetails]
//   );

//   const { shifts, loading, error } = useSelector((state: RootState) => state.clockInOut);
//   const [selectedShift, setSelectedShift] = useState<Shift | null>(null);
//   const [upcomingShift, setUpcomingShift] = useState<Shift | null>(null);
//   const [isShiftStarted, setIsShiftStarted] = useState(false);
//   const [elapsedTimeInSeconds, setElapsedTimeInSeconds] = useState(0);
//   const [totalTimeWorked, setTotalTimeWorked] = useState<string | null>(null);
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [isShiftNotesModalOpen, setIsShiftNotesModalOpen] = useState(false);
//   const [historyView, setHistoryView] = useState<'day' | 'week' | 'month' | 'year'>('day');
//   const [clockInLoading, setClockInLoading] = useState(false);
//   const [clockOutLoading, setClockOutLoading] = useState(false);
//   const [snackbar, setSnackbar] = useState<{
//     open: boolean;
//     message: string;
//     severity: 'success' | 'error';
//   }>({
//     open: false,
//     message: '',
//     severity: 'success'
//   });
//   const [clockInTime, setClockInTime] = useState<string | null>(null);

//   // Format date from ISO timestamp
//   const formatDate = (isoTimestamp: string | undefined): string => {
//     if (!isoTimestamp) return '';
//     try {
//       const normalized = normalizeTimestamp(isoTimestamp);
//       return format(parseISO(normalized!), 'dd/MM/yy');
//     } catch {
//       return 'Invalid date';
//     }
//   };

//   // Fetch shifts from API
//   useEffect(() => {
//     dispatch(fetchShifts(basePayload));
//   }, [dispatch]);

//   // Set default and upcoming shifts
//   useEffect(() => {
//     if (shifts.length > 0 && !selectedShift) {
//       const today = format(new Date(), 'yyyy-MM-dd');
//       const defaultShift =
//         shifts.find((shift) => {
//           try {
//             const normalizedStart = normalizeTimestamp(shift.start);
//             return (
//               !shift.actual_end &&
//               shift.start &&
//               format(parseISO(normalizedStart!), 'yyyy-MM-dd') === today
//             );
//           } catch {
//             return false;
//           }
//         }) || shifts[0];
//       const otherShift = shifts.find((shift) => shift.id !== defaultShift?.id) || null;
//       setSelectedShift(defaultShift || null);
//       setUpcomingShift(otherShift);
//       setIsShiftStarted(!!defaultShift?.actual_start && !defaultShift?.actual_end);
//       setClockInTime(defaultShift?.actual_start || null);
//       setTotalTimeWorked(
//         defaultShift?.actual_worked_hours && defaultShift?.actual_end
//           ? `${defaultShift.actual_worked_hours} hours`
//           : null
//       );

//       // Calculate elapsed time for started or completed shifts
//       if (defaultShift?.actual_start) {
//         try {
//           const normalizedStart = normalizeTimestamp(defaultShift.actual_start);
//           const startTime = parseISO(normalizedStart!);
//           const endTime = defaultShift.actual_end
//             ? parseISO(normalizeTimestamp(defaultShift.actual_end)!)
//             : new Date();
//           const elapsed = differenceInSeconds(endTime, startTime);
//           setElapsedTimeInSeconds(elapsed > 0 ? elapsed : 0);
//         } catch {
//           setElapsedTimeInSeconds(0);
//         }
//       }
//     }
//   }, [shifts, selectedShift]);

//   // Timer for elapsed time
//   useEffect(() => {
//     let interval: NodeJS.Timeout | null = null;
//     if (isShiftStarted && clockInTime && !selectedShift?.actual_end) {
//       interval = setInterval(() => {
//         try {
//           const normalizedStart = normalizeTimestamp(clockInTime);
//           const startTime = parseISO(normalizedStart!);
//           const currentTime = new Date();
//           const elapsed = differenceInSeconds(currentTime, startTime);
//           setElapsedTimeInSeconds(elapsed > 0 ? elapsed : 0);
//         } catch {
//           setElapsedTimeInSeconds(0);
//         }
//       }, 1000);
//     }
//     return () => {
//       if (interval) clearInterval(interval);
//     };
//   }, [isShiftStarted, clockInTime, selectedShift?.actual_end]);

//   const handleOpenModal = () => setIsModalOpen(true);
//   const handleCloseModal = () => setIsModalOpen(false);

//   const handleClockIn = async () => {
//     if (!selectedShift) {
//       setSnackbar({ open: true, message: 'No shift selected', severity: 'error' });
//       return;
//     }

//     try {
//       const normalizedStart = normalizeTimestamp(selectedShift.start);
//       const shiftStart = parseISO(normalizedStart!);
//       const currentTime = new Date();
//       if (currentTime < shiftStart) {
//         setSnackbar({
//           open: true,
//           message: 'Cannot clock in before shift start time',
//           severity: 'error'
//         });
//         return;
//       }
//     } catch {
//       setSnackbar({ open: true, message: 'Invalid shift start time', severity: 'error' });
//       return;
//     }

//     setClockInLoading(true);
//     try {
//       const clockInTime = new Date().toISOString();
//       const updatedShift: Shift = {
//         ...selectedShift,
//         actual_start: clockInTime,
//         actual_worked_hours: '0',
//         clockInTime
//       };

//       const clockInPayload: ClockInOutPayload = {
//         ...basePayload,
//         shiftId: selectedShift.id,
//         userId: basePayload.loggedIN_user_id,
//         clockInTime,
//         actual_start: clockInTime,
//         actual_worked_hours: '0',
//         event_id: selectedShift.id,
//         title: selectedShift.title,
//         description: selectedShift.description || '',
//         start: selectedShift.start,
//         end: selectedShift.end,
//         support_item_number: selectedShift.support_item_number || '',
//         support_worder: selectedShift.support_worder || '',
//         manager: selectedShift.manager || '',
//         client_name: selectedShift.client_name || '',
//         color: selectedShift.color || '#2196f3',
//         textColor: selectedShift.textColor || '',
//         allDay: selectedShift.allDay || false
//       };

//       await dispatch(
//         clockIn({
//           shift: selectedShift,
//           clockInTime,
//           payload: clockInPayload
//         })
//       ).unwrap();

//       setIsShiftStarted(true);
//       setClockInTime(clockInTime);
//       setElapsedTimeInSeconds(0);
//       setTotalTimeWorked(null);
//       setSelectedShift(updatedShift);
//       setSnackbar({ open: true, message: 'Successfully clocked in!', severity: 'success' });
//     } catch (error: any) {
//       setSnackbar({
//         open: true,
//         message: error.message || 'Failed to clock in',
//         severity: 'error'
//       });
//     } finally {
//       setClockInLoading(false);
//     }
//   };

//   const handleClockOut = async () => {
//     if (!selectedShift || !clockInTime) {
//       setSnackbar({
//         open: true,
//         message: 'Shift not started or no shift selected',
//         severity: 'error'
//       });
//       return;
//     }

//     try {
//       const normalizedEnd = normalizeTimestamp(selectedShift.end);
//       const shiftEnd = parseISO(normalizedEnd!);
//       const currentTime = new Date();
//       if (currentTime < shiftEnd) {
//         setSnackbar({
//           open: true,
//           message: 'Cannot clock out before shift end time',
//           severity: 'error'
//         });
//         return;
//       }
//     } catch {
//       setSnackbar({ open: true, message: 'Invalid shift end time', severity: 'error' });
//       return;
//     }

//     setClockOutLoading(true);
//     try {
//       const clockOutTime = new Date().toISOString();
//       const diffInHours = (
//         (new Date(clockOutTime).getTime() - new Date(clockInTime).getTime()) /
//         (1000 * 60 * 60)
//       ).toFixed(2);
//       const diffInSeconds = differenceInSeconds(new Date(clockOutTime), new Date(clockInTime));
//       const updatedShift: Shift = {
//         ...selectedShift,
//         actual_end: clockOutTime,
//         actual_worked_hours: diffInHours,
//         clockOutTime
//       };

//       const clockOutPayload: ClockInOutPayload = {
//         ...basePayload,
//         shiftId: selectedShift.id,
//         userId: basePayload.loggedIN_user_id,
//         clockOutTime,
//         actual_end: clockOutTime,
//         actual_worked_hours: diffInHours,
//         event_id: selectedShift.id,
//         title: selectedShift.title,
//         description: selectedShift.description || '',
//         start: selectedShift.start,
//         end: selectedShift.end,
//         support_item_number: selectedShift.support_item_number || '',
//         support_worder: selectedShift.support_worder || '',
//         manager: selectedShift.manager || '',
//         client_name: selectedShift.client_name || '',
//         color: selectedShift.color || '#2196f3',
//         textColor: selectedShift.textColor || '',
//         allDay: selectedShift.allDay || false
//       };

//       await dispatch(
//         clockOut({
//           shift: selectedShift,
//           clockOutTime,
//           clockInTime,
//           payload: clockOutPayload
//         })
//       ).unwrap();

//       setIsShiftStarted(false);
//       setTotalTimeWorked(`${diffInHours} hours`);
//       setClockInTime(null);
//       setElapsedTimeInSeconds(diffInSeconds > 0 ? diffInSeconds : 0);
//       setSelectedShift(updatedShift);
//       setSnackbar({ open: true, message: 'Successfully clocked out!', severity: 'success' });
//       setIsShiftNotesModalOpen(true);
//     } catch (error: any) {
//       setSnackbar({
//         open: true,
//         message: error.message || 'Failed to clock out',
//         severity: 'error'
//       });
//     } finally {
//       setClockOutLoading(false);
//     }
//   };

//   const handleShiftNotesModalClose = () => {
//     setIsShiftNotesModalOpen(false);
//   };

//   const handleFillShiftNotes = () => {
//     setIsShiftNotesModalOpen(false);
//     navigate('/profile/ShiftNotes', { state: { selectedShift } });
//   };

//   const formatElapsedTime = (seconds: number) => {
//     const hours = Math.floor(seconds / 3600);
//     const minutes = Math.floor((seconds % 3600) / 60);
//     return `${hours}h : ${minutes}m`;
//   };

//   const handleSnackbarClose = () => {
//     setSnackbar({ ...snackbar, open: false });
//     dispatch(resetError());
//   };

//   const handleShiftChange = (event: any) => {
//     const shiftId = event.target.value;
//     const shift = shifts.find((s) => s.id === shiftId) || null;
//     const otherShift = shifts.find((s) => s.id !== shiftId) || null;
//     setSelectedShift(shift);
//     setUpcomingShift(otherShift);
//     setIsShiftStarted(!!shift?.actual_start && !shift?.actual_end);
//     setClockInTime(shift?.actual_start || null);
//     setTotalTimeWorked(
//       shift?.actual_worked_hours && shift?.actual_end ? `${shift.actual_worked_hours} hours` : null
//     );

//     if (shift?.actual_start) {
//       try {
//         const normalizedStart = normalizeTimestamp(shift.actual_start);
//         const startTime = parseISO(normalizedStart!);
//         const endTime = shift.actual_end
//           ? parseISO(normalizeTimestamp(shift.actual_end)!)
//           : new Date();
//         const elapsed = differenceInSeconds(endTime, startTime);
//         setElapsedTimeInSeconds(elapsed > 0 ? elapsed : 0);
//       } catch {
//         setElapsedTimeInSeconds(0);
//       }
//     } else {
//       setElapsedTimeInSeconds(0);
//     }
//   };

//   const calculateTotalHoursProjected = (shift: Shift | null): string => {
//     if (!shift?.start || !shift?.end) return '8';
//     try {
//       const normalizedStart = normalizeTimestamp(shift.start);
//       const normalizedEnd = normalizeTimestamp(shift.end);
//       const start = parseISO(normalizedStart!);
//       const end = parseISO(normalizedEnd!);
//       const diffInMs = end.getTime() - start.getTime();
//       return (diffInMs / (1000 * 60 * 60)).toFixed(2);
//     } catch {
//       return '8';
//     }
//   };

//   return (
//     <Box sx={{ minHeight: '100vh', p: { xs: 1, md: 3 } }}>
//       <Grid container spacing={{ xs: 1, md: 3 }}>
//         <Grid item xs={12}>
//           <Box mb={2}>
//             <FormControl fullWidth>
//               <InputLabel>Select Shift</InputLabel>
//               <Select
//                 value={selectedShift?.id || ''}
//                 onChange={handleShiftChange}
//                 label="Select Shift"
//                 disabled={loading}
//               >
//                 {shifts.length === 0 && (
//                   <MenuItem value="" disabled>
//                     No shifts available
//                   </MenuItem>
//                 )}
//                 {shifts.map((shift) => (
//                   <MenuItem key={shift.id} value={shift.id}>
//                     {shift.title} ({shift.start ? formatDate(shift.start) : 'N/A'})
//                   </MenuItem>
//                 ))}
//               </Select>
//             </FormControl>
//           </Box>
//           {loading && (
//             <Box display="flex" justifyContent="center" my={2}>
//               <CircularProgress />
//             </Box>
//           )}
//           <JobDetails
//             shift={selectedShift}
//             upcomingShift={upcomingShift}
//             handleOpenModal={handleOpenModal}
//             formatDate={formatDate}
//           />
//         </Grid>
//         <Grid item xs={12} md={6}>
//           <TimeTracker
//             isShiftStarted={isShiftStarted}
//             elapsedTimeInSeconds={elapsedTimeInSeconds}
//             totalTimeWorked={totalTimeWorked}
//             handleClockIn={handleClockIn}
//             handleClockOut={handleClockOut}
//             formatElapsedTime={formatElapsedTime}
//             clockInLoading={clockInLoading}
//             clockOutLoading={clockOutLoading}
//             totalHoursProjected={calculateTotalHoursProjected(selectedShift)}
//             isShiftCompleted={!!selectedShift?.actual_end}
//           />
//         </Grid>
//         <Grid item xs={12} md={6}>
//           <OtherDetails />
//         </Grid>
//         <Grid item xs={12}>
//           <EmployeeHistory historyView={historyView} setHistoryView={setHistoryView} />
//         </Grid>
//       </Grid>
//       <Dialog
//         open={isModalOpen}
//         onClose={handleCloseModal}
//         maxWidth="lg"
//         fullWidth
//         fullScreen={isMobile}
//       >
//         <DialogTitle>
//           <IconButton
//             aria-label="close"
//             onClick={handleCloseModal}
//             sx={{
//               position: 'absolute',
//               right: 16,
//               top: 16,
//               color: (theme) => theme.palette.grey[500],
//               borderRadius: '50%'
//             }}
//           >
//             <CloseIcon />
//           </IconButton>
//         </DialogTitle>
//         <DialogContent sx={{ mt: 2, p: { xs: 2, md: 3 } }}>
//           <ShiftDetailsTable />
//         </DialogContent>
//       </Dialog>
//       <Dialog
//         open={isShiftNotesModalOpen}
//         onClose={handleShiftNotesModalClose}
//         maxWidth="sm"
//         fullWidth
//       >
//         <DialogTitle>Shift Notes</DialogTitle>
//         <DialogContent>
//           <Typography variant="body1">
//             You have successfully clocked out. Would you like to fill out shift notes now?
//           </Typography>
//         </DialogContent>
//         <DialogActions>
//           <Button onClick={handleShiftNotesModalClose} color="primary">
//             No, Later
//           </Button>
//           <Button onClick={handleFillShiftNotes} color="primary" variant="contained">
//             Yes, Fill Now
//           </Button>
//         </DialogActions>
//       </Dialog>
//       <Snackbar
//         open={snackbar.open || !!error}
//         autoHideDuration={6000}
//         onClose={handleSnackbarClose}
//         anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
//       >
//         <Alert
//           onClose={handleSnackbarClose}
//           severity={error ? 'error' : snackbar.severity}
//           sx={{ width: '100%' }}
//         >
//           {error || snackbar.message}
//         </Alert>
//       </Snackbar>
//     </Box>
//   );
// };

// export default ClockInOut;
